import { cva, type VariantProps } from 'class-variance-authority';
import { EyeIcon, EyeOffIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

const colorVariants = cva(
  'flex h-9 w-full items-center justify-between gap-2 rounded-sm border-2 px-3 py-1 whitespace-nowrap text-white transition-colors focus:ring-2',
  {
    variants: {
      color: {
        emerald:
          'border-emerald-500 bg-emerald-500 hover:bg-emerald-400 focus:ring-emerald-300',
        red: 'border-red-500 bg-red-500 hover:bg-red-400 focus:ring-red-300',
        orange:
          'border-orange-500 bg-orange-500 hover:bg-orange-400 focus:ring-orange-300',
        amber:
          'border-amber-500 bg-amber-500 hover:bg-amber-400 focus:ring-amber-300',
        yellow:
          'border-yellow-500 bg-yellow-500 hover:bg-yellow-400 focus:ring-yellow-300',
        lime: 'border-lime-500 bg-lime-500 hover:bg-lime-400 focus:ring-lime-300',
        green:
          'border-green-500 bg-green-500 hover:bg-green-400 focus:ring-green-300',
        teal: 'border-teal-500 bg-teal-500 hover:bg-teal-400 focus:ring-teal-300',
        cyan: 'border-cyan-500 bg-cyan-500 hover:bg-cyan-400 focus:ring-cyan-300',
        sky: 'border-sky-500 bg-sky-500 hover:bg-sky-400 focus:ring-sky-300',
        blue: 'border-blue-500 bg-blue-500 hover:bg-blue-400 focus:ring-blue-300',
        indigo:
          'border-indigo-500 bg-indigo-500 hover:bg-indigo-400 focus:ring-indigo-300',
        violet:
          'border-violet-500 bg-violet-500 hover:bg-violet-400 focus:ring-violet-300',
        purple:
          'border-purple-500 bg-purple-500 hover:bg-purple-400 focus:ring-purple-300',
        fuchsia:
          'border-fuchsia-500 bg-fuchsia-500 hover:bg-fuchsia-400 focus:ring-fuchsia-300',
        pink: 'border-pink-500 bg-pink-500 hover:bg-pink-400 focus:ring-pink-300',
        rose: 'border-rose-500 bg-rose-500 hover:bg-rose-400 focus:ring-rose-300',
      },
      outlined: {
        false: null,
        true: 'bg-white',
      },
    },
    compoundVariants: [
      {
        color: 'emerald',
        outlined: true,
        className: 'text-emerald-500 hover:bg-emerald-50',
      },
      {
        color: 'red',
        outlined: true,
        className: 'text-red-500 hover:bg-red-50',
      },
      {
        color: 'orange',
        outlined: true,
        className: 'text-orange-500 hover:bg-orange-50',
      },
      {
        color: 'amber',
        outlined: true,
        className: 'text-amber-500 hover:bg-amber-50',
      },
      {
        color: 'yellow',
        outlined: true,
        className: 'text-yellow-500 hover:bg-yellow-50',
      },
      {
        color: 'yellow',
        outlined: true,
        className: 'text-yellow-500 hover:bg-yellow-50',
      },
      {
        color: 'lime',
        outlined: true,
        className: 'text-lime-500 hover:bg-lime-50',
      },
      {
        color: 'green',
        outlined: true,
        className: 'text-green-500 hover:bg-green-50',
      },
      {
        color: 'teal',
        outlined: true,
        className: 'text-teal-500 hover:bg-teal-50',
      },
      {
        color: 'cyan',
        outlined: true,
        className: 'text-cyan-500 hover:bg-cyan-50',
      },
      {
        color: 'sky',
        outlined: true,
        className: 'text-sky-500 hover:bg-sky-50',
      },
      {
        color: 'blue',
        outlined: true,
        className: 'text-blue-500 hover:bg-blue-50',
      },
      {
        color: 'indigo',
        outlined: true,
        className: 'text-indigo-500 hover:bg-indigo-50',
      },
      {
        color: 'violet',
        outlined: true,
        className: 'text-violet-500 hover:bg-violet-50',
      },
      {
        color: 'purple',
        outlined: true,
        className: 'text-purple-500 hover:bg-purple-50',
      },
      {
        color: 'fuchsia',
        outlined: true,
        className: 'text-fuchsia-500 hover:bg-fuchsia-50',
      },
      {
        color: 'pink',
        outlined: true,
        className: 'text-pink-500 hover:bg-pink-50',
      },
      {
        color: 'rose',
        outlined: true,
        className: 'text-rose-500 hover:bg-rose-50',
      },
    ],
    defaultVariants: { color: 'emerald' },
  },
);

export type TailwindColor = NonNullable<
  VariantProps<typeof colorVariants>['color']
>;
export function UserToggle({
  color,
  colorHex,
  className,
  children,
  outlined,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof colorVariants> & { colorHex?: string | null }) {
  const inlineColorStyles = colorHex
    ? outlined
      ? {
          borderColor: colorHex,
          color: colorHex,
          backgroundColor: '#ffffff',
        }
      : {
          borderColor: colorHex,
          backgroundColor: colorHex,
          color: '#ffffff',
        }
    : undefined;

  return (
    <button
      type="button"
      {...props}
      className={cn(colorVariants({ className, color, outlined }))}
      style={inlineColorStyles}
    >
      <span className="min-w-0 flex-1 truncate text-left">{children}</span>
      {outlined ? (
        <EyeOffIcon className="size-4 shrink-0" />
      ) : (
        <EyeIcon className="size-4 shrink-0" />
      )}
    </button>
  );
}
